import {CountryPicker} from "../../CountryPicker/CountryPicker";
import {AsYouType, parsePhoneNumber, parsePhoneNumberFromString} from "libphonenumber-js/max";
import {useEffect, useRef, useState} from "react";
import {FlagImage} from "react-international-phone";
import {CountryCode} from "libphonenumber-js/max";
import {PhoneHQInput} from 'ch3-ui-lib';
import {getCallingCountries} from "../services/countryDataService";

interface Country {
    name: string;
    alpha2: string;
    countryCallingCodes: readonly string[];
    emoji: string;
}

interface CallInputProps {
    phoneNumber: string | undefined;
    onPhoneNumberChange: (phoneNumber: number) => void;
    onPhoneNumberClear?: () => void;
    onBlur?: () => void;
    error?: string | undefined;
    name?: string;
    phqId?: string;
    disabled?: boolean;
    hideLabel?: boolean;
}

export const PhoneNumberInput = ({phoneNumber, onPhoneNumberChange, onPhoneNumberClear, error, onBlur, name, phqId, disabled, hideLabel}: CallInputProps) => {
    const [typedNumber, setTypedNumber] = useState(phoneNumber);
    const lastEmittedPhoneNumberRef = useRef<string | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<{   isoCountry: string;   prefix: string; } | null>(
        {
            isoCountry: 'US',
            prefix: '+1',
        }
    );


    useEffect(() => {
        if (!phoneNumber) {
            if (lastEmittedPhoneNumberRef.current === '') {
                // The field cleared itself while typing; keep what is shown (e.g. a lone "+").
                lastEmittedPhoneNumberRef.current = null;
                return;
            }
            setTypedNumber('');
            return;
        }
        if (lastEmittedPhoneNumberRef.current === phoneNumber.toString()) {
            return;
        }
        try {
            const parsedPhoneNumber = parsePhoneNumber(`+${phoneNumber}`);
            const country = getCallingCountries().find(
                (country) => country.alpha2 === parsedPhoneNumber?.country);
            setTypedNumber(new AsYouType(country?.alpha2 as CountryCode).input(parsedPhoneNumber.nationalNumber));
            setSelectedCountry({
                isoCountry: country?.alpha2 ?? 'US',
                prefix: country?.countryCallingCodes[0] ?? '+1',
            });
        } catch (error) {
            setTypedNumber(phoneNumber.toString());
        }

    }, [phoneNumber]);

    const handleCountrySelect = (country: Pick<Country, "alpha2" | "countryCallingCodes">) => {


        setSelectedCountry({
            isoCountry: country.alpha2,
            prefix: country.countryCallingCodes[0],
        })

        if (typedNumber) {
            const phoneNumberParsed = parsePhoneNumber(`${country.countryCallingCodes[0] + typedNumber?.replace(/\D/g, '')}`);

            setTypedNumber(new AsYouType(country.alpha2 as CountryCode).input(phoneNumberParsed?.nationalNumber));
            const nextPhoneNumber = phoneNumberParsed?.number.replace(/\D/g, '');
            lastEmittedPhoneNumberRef.current = nextPhoneNumber;
            onPhoneNumberChange(Number(nextPhoneNumber));

        } else {
            setTypedNumber('');
        }
    };

    const emit = (digits: string) => {
        lastEmittedPhoneNumberRef.current = digits;
        onPhoneNumberChange(Number(digits));
    };

    const clear = () => {
        // Remember that the field emptied itself, so the parent's '' doesn't wipe a lone "+" the user just typed.
        lastEmittedPhoneNumberRef.current = '';
        if (onPhoneNumberClear) {
            onPhoneNumberClear();
        } else {
            onPhoneNumberChange(0);
        }
    };

    const handlePhoneNumberChange = (val: string) => {
        const trimmed = val.trim();
        const international = trimmed.startsWith('+') || trimmed.startsWith('00');
        const digits = (international ? trimmed.replace(/^(\+|00)/, '') : trimmed).replace(/\D/g, '');
        const prefixDigits = selectedCountry?.prefix.replace(/\D/g, '') ?? '';

        if (digits === '') {
            // Keep a lone "+" so the user can go on typing the country code.
            setTypedNumber(international ? '+' : '');
            clear();
            return;
        }

        if (international) {
            // "+48 600…" / "0048 600…": the flag follows the country code as it is typed.
            const typer = new AsYouType();
            const formatted = typer.input(`+${digits}`);
            const callingCode = typer.getCallingCode();
            if (callingCode && callingCode !== prefixDigits) {
                const country = typer.getCountry()
                    ?? getCallingCountries().find((c) => c.countryCallingCodes.includes(`+${callingCode}`))?.alpha2;
                if (country) setSelectedCountry({isoCountry: country, prefix: `+${callingCode}`});
            }
            setTypedNumber(formatted);
            emit(digits);
            return;
        }

        // Without "+": a national number for the chosen flag. If it already starts with that flag's
        // country code and is a valid number that way (e.g. 48600123456 with PL), keep it as typed.
        const withCode = parsePhoneNumberFromString(`+${digits}`);
        if (prefixDigits && digits.startsWith(prefixDigits) && withCode?.isValid() && withCode.countryCallingCode === prefixDigits) {
            setTypedNumber(val);
            emit(digits);
            return;
        }
        setTypedNumber(new AsYouType(selectedCountry?.isoCountry as CountryCode).input(val));
        emit(`${prefixDigits}${digits}`);
    };

    return (
        <div className='w-full'>
            {!hideLabel && <label className='text-sm'>Phone number</label>}
            <div className='flex items-end mb-2 w-full items-end' >

                <CountryPicker
                    value={selectedCountry?.isoCountry}
                    onSelect={handleCountrySelect}
                    trigger={
                        <button type='button' aria-label='Select country' className='mr-2 flex cursor-pointer items-start justify-center rounded-control border-0 bg-transparent p-0 hover:bg-surface-muted'>
                            <FlagImage size={44} iso2={selectedCountry?.isoCountry.toLowerCase()} className={'px-2 py-2'}/>
                        </button>
                    }
                />
                <div className='w-full'>
                    <PhoneHQInput
                        phq-id={phqId ?? `dialpad-typeNumberInput`}
                        containerClassName='w-full'
                        type='tel'
                        value={typedNumber}
                        onChange={(e) => handlePhoneNumberChange(e.target.value)}
                        onBlur={onBlur}
                        name={name}
                        disabled={disabled}
                    />
                </div>

            </div>
            {(typeof error === 'string') && <div className='pt-2 text-red-500 text-sm'>{error}</div>}
        </div>


    );
};
